/* eslint-disable prefer-destructuring */
import { CustomException, elk, language, request } from '@tencent/ppd-base-libs';
import { jumpPage } from '@tencent/wphk-components-jump-page';
import { getSPInfoBySPId } from '@tencent/wphk-components-wecoupon/components/mch-coupon-list/api';
import { isEmpty, omit } from 'lodash-es';
import { defineStore } from 'pinia';
import { BankActItemCfg, CouponItemCfg, MerchantBaseInfo, MerchantType, OriginalMerchantBaseInfo } from '../types/merchant-info';
import { getUrlParamByKey, UrlParamKey } from '../utils/url-params-helper';
import { to, cacheFunc } from '@tencent/wphk-base-utils';
import { MerchantInfo } from '@tencent/wphk-components-wecoupon/type';
import { SHARE_URL_TAG } from '@tencent/wphk-components-wecoupon/components/share-channel/business/share-helper';

const initMerchantConfig: MerchantBaseInfo = {
  mrchId: '',
  mrchName: '',
  mrchLogo: '',
  backgroundImg: '',
  spId: '',
  simpleCouponList: [] as CouponItemCfg[],
  bankCouponList: [] as BankActItemCfg[],
  chainCouponList: [] as CouponItemCfg[],
  mrchType: MerchantType.single,
};

export const useMerchantInfo = defineStore('merchantInfo', {
  state: () => ({
    merchantConfig: initMerchantConfig,
    spId: getUrlParamByKey(UrlParamKey.spId),
    mtn: getUrlParamByKey(UrlParamKey.mtn) || getUrlParamByKey(UrlParamKey.MTN),
  }),
  getters: {
    merchantId(state) {
      return state.merchantConfig.mrchId;
    },
    /**
     * @TAPD https://tapd.woa.com/10145891/prong/stories/view/1010145891886023997
     * 判断当前是否需要展示对应活动组件（新人组件和印花狂赏组件）
     * 需求中，要考虑从物料平台过来的情况，url中带MTN参数时，只有mall展示该组件。因此，把逻辑改为
     * (isMall && hasMtn && !isShare) || !isMall
     * 比较合理。非mall类型的，是以前老的逻辑，不应该受本次需求的影响
     */
    isNeedShowAct(state) {
      const isShare = getUrlParamByKey(UrlParamKey.fromtag) === SHARE_URL_TAG;
      const hasMtn = !!state.mtn;
      const isMall = state.merchantConfig.mrchType === MerchantType.mall;
      return !isMall || (isMall && hasMtn && !isShare);
    },
    isMall(state) {
      return state.merchantConfig.mrchType === MerchantType.mall;
    },
  },
  actions: {
    /**
      * 获取商户信息
      * @param merchantId 商户id
      */
    async initMerchantInfo() {
      checkAndReportUrl();
      // 如果this.spId为不为空
      if (this.spId) {
        this.merchantConfig = await getMerchantInfo(this.spId);
        return;
      }
      // 如果this.spId为空：url上没有spId而只有merchantId，则从配置取spId
      const merchantId = getUrlParamByKey('merchantId' as any);
      const targetConf = await getMerchantConfByMerchantId(merchantId);
      this.spId = targetConf?.spId || '';
      if (!targetConf) {
        // 处理未找到商户配置
        handleMerchantNotFound({ merchantId });
      } else if (this.spId) {
        // 如果spId不为空，则合并获取抢券有so商户配置和全城狂赏货源配置
        this.merchantConfig = await getMerchantInfo(this.spId);
      } else {
        // 这种兜底处理对于分享（需要spId）会有问题，但是不打算处理分享场景了
        this.merchantConfig = formatMerchantConfig(targetConf);
        // 如果spId是空，则告警
        new CustomException('', 'spIdEmpty', `找不到商户spId, mrchId:${merchantId}`);
      }
    },
  },
});

/** 初始化商戶信息之前都觸發一次elk上報，為了排查目前是否還有來自merchantId的鏈接 **/
const checkAndReportUrl = () => {
  const merchantId = getUrlParamByKey('merchantId' as any);
  elk({
    key: 'reportUrl',
    str1: `merchantId:${merchantId}`,
    msg: window.location.href,
  });
};

/**
 * 获取商户所有配置: 整合抢券有so商户配置和全城狂赏货源配置
 */
const getMerchantInfo = async (spId: string): Promise<MerchantBaseInfo> => {
  const [spInfo, merchantInfo] = await Promise.all([
    safeGetSPInfoBySPId(spId), getMerchantConfBySpId(spId)]);
  console.log('spInfo, merchantInfo', spInfo, merchantInfo);
  // getSPInfoBySPId 中获取 spLogo, spName， 如果为空采用getMerchantConfBySpId的商户信息
  const { mrchName, mrchLogo } = merchantInfo as MerchantBaseInfo;
  const { spName, spLogo } = spInfo;
  const result = {
    ...merchantInfo,
    mrchLogo: spLogo || mrchLogo,
    mrchName: spName || mrchName,
  };
  return result;
};

/**
 * 调用抢券有so商户信息接口不报错
 * 为了兼容：在货源配了全城狂赏配置，但是没有配置抢券有so商户信息的异常情况
 */
const safeGetSPInfoBySPId = async (spId: string): Promise<MerchantInfo> => {
  const [err, res] = await to(getSPInfoBySPId(spId));
  if (err || !res) {
    return {} as any;
  }
  return res;
};

/** 根据merchantId获取货源商户配置 */
const getMerchantConfByMerchantId = async (merchantId: string): Promise<OriginalMerchantBaseInfo | undefined> => {
  const [err, res] = await to(getMerchantConfig());
  if (err || !res) {
    return;
  }
  const { merchantInfo: merchantList = [] } = res;
  const targetMrchConf = merchantList.find(item => item.mrchId === merchantId);
  return targetMrchConf;
};

/** 根据spId获取货源商户配置 */
const getMerchantConfBySpId = async (spId: string): Promise<MerchantBaseInfo> => {
  const { merchantInfo: merchantList = [] } = await getMerchantConfig();
  const targetSpIdConfList = merchantList.filter(item => item.spId === spId);
  const targetListLength = targetSpIdConfList.length;
  let resultMrchConf: OriginalMerchantBaseInfo = {} as any;
  if (targetListLength === 0) {
    // 处理未找到商户配置
    handleMerchantNotFound({ spId });
  } if (targetListLength === 1) {
    // 长度为1，说明是一对一，取第一条
    resultMrchConf = targetSpIdConfList[0];
  } else {
    // 长度大于1，说明是一对多，那么取 CM开头的/纯数字
    resultMrchConf = getCMPrefixOrNumberMrchInfo(spId, targetSpIdConfList);
  }
  return formatMerchantConfig(resultMrchConf);
};

/** 如果匹配到商户配置列表，长度大于1：
 * 那么优先取CM-开头的merchantId商户配置
 * 其次取纯数字的merchantId商户配置
 * */
const getCMPrefixOrNumberMrchInfo = (spId: string, mrchList: OriginalMerchantBaseInfo[]) => {
  const cmPrefixReg = new RegExp(/^CM-/g);
  const numberReg = new RegExp(/^(\d)+$/);
  const cmPrefixRes = mrchList.filter(item => cmPrefixReg.test(item.mrchId));
  // 优先取CM-开头的
  if (!isEmpty(cmPrefixRes)) {
    return cmPrefixRes[0];
  }
  // 其次取纯数字的
  const numberRes = mrchList.filter(item => numberReg.test(item.mrchId));
  if (!isEmpty(numberRes)) {
    return numberRes[0];
  }
  // 不符合以上两个规则的，兜底返回，并上报
  new CustomException(mrchList.map(item => item.mrchId), 'merchantRuleNotMatch', `商户配置规则不匹配,spId:${spId}`);
  return mrchList[0];
};

/** 获取商户配置 - 货源配置系统商户配置 */
const getMerchantConfig = cacheFunc((): Promise<{ merchantInfo: OriginalMerchantBaseInfo[] }> => request.loadConfig('https://st.moneydata.hk/res/cc/MerchantGiftpack/config.json'));

/** 格式化货源配置系统商户配置：处理商户名称和背景图的多语言 */
const formatMerchantConfig = (config: OriginalMerchantBaseInfo): MerchantBaseInfo => {
  if (!config) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {} as MerchantBaseInfo;
  }
  const omitLangKeys = ['mrchNameEn', 'backgroundImgEn'];
  const configWithLang = {
    ...omit(config, omitLangKeys) as MerchantBaseInfo,
    mrchName: getKeyWithLang({ key: 'mrchName', obj: config }),
    backgroundImg: getKeyWithLang({ key: 'backgroundImg', obj: config }),
    mrchType: +config.mrchType || MerchantType.single,
  };
  return configWithLang;
};

const getKeyWithLang = ({ key, obj }: { key: string, obj: object }) => {
  const targetKey = language.isEn() ? `${key}En` : key;
  return obj[targetKey] || obj[key];
};

/** 处理未找到商户配置 */
const handleMerchantNotFound = ({ spId = '', merchantId = '' }: {spId?: string, merchantId?: string}) => {
  // customException上报可能会由于跳转而abort，所以在adtag上做一次记录
  jumpPage.goDiscountCenter({ adtag: `channel-${spId}-${merchantId}`, delay: 500, isReplace: true });
  throw new CustomException('', 'merchantNotFound', `未找到商戶配置,spId:${spId},mrchId:${merchantId}`);
};
